import { createClient } from '@supabase/supabase-js';

const URL = process.env.REACT_APP_SUPABASE_URL;
const ANON = process.env.REACT_APP_SUPABASE_ANON_KEY;
export const syncEnabled = !!URL && !!ANON;

// Stable operator id. Sent as a header so RLS scopes every row to you.
// This is effectively your data key — the same id on any browser/device pulls
// the same data. Resolution order:
//   1. REACT_APP_GHXST_USER_ID  — a fixed id baked into the build. When set,
//      EVERY browser/device that loads this build shares one identity, so your
//      data follows you everywhere and can never be stranded behind a random
//      per-browser id. This is the recommended setup for a single-operator app.
//   2. localStorage 'ghxst_user_id' — a previously pinned/generated id.
//   3. A freshly generated id, persisted locally (legacy per-device behaviour).
const FIXED_ID = (process.env.REACT_APP_GHXST_USER_ID || '').trim();
function getUserId() {
  if (FIXED_ID) {
    // Keep localStorage in sync so console helpers and any legacy reads agree.
    if (localStorage.getItem('ghxst_user_id') !== FIXED_ID) {
      localStorage.setItem('ghxst_user_id', FIXED_ID);
    }
    return FIXED_ID;
  }
  let id = localStorage.getItem('ghxst_user_id');
  if (!id) {
    id = 'snxper-' + crypto.randomUUID();
    localStorage.setItem('ghxst_user_id', id);
  }
  return id;
}
const USER_ID = syncEnabled ? getUserId() : null;

export const supabase = syncEnabled
  ? createClient(URL, ANON, { global: { headers: { 'x-ghxst-user': USER_ID } } })
  : null;

// The id this session is reading/writing under. Handy for the console + UI.
export function currentUserId() { return USER_ID; }

// Recovery: pin this browser to a known id (e.g. copied from another device),
// then reload so it pulls that identity's data from the cloud. If a FIXED_ID is
// baked into the build it always wins on next load, so this is a no-op there.
export function setUserId(id) {
  const clean = (id || '').trim();
  if (!clean) return false;
  localStorage.setItem('ghxst_user_id', clean);
  return true;
}

// Mirror one key to Supabase. Debounced per-key so rapid edits don't spam the network.
const timers = {};
export function pushKey(fullKey, rawValue) {
  if (!syncEnabled) return;
  clearTimeout(timers[fullKey]);
  timers[fullKey] = setTimeout(async () => {
    try {
      let value; try { value = JSON.parse(rawValue); } catch { value = rawValue; }
      const { error } = await supabase.from('ghxst_store').upsert({
        id: USER_ID + '::' + fullKey, user_id: USER_ID, value, updated_at: new Date().toISOString(),
      });
      if (error) console.error('[GHXST Sync] push failed', fullKey, error.message);
    } catch (err) { console.error('[GHXST Sync] push error', err); }
  }, 600);
}

// Pull all rows on load and write them into localStorage. Returns true if data was found.
export async function pullAll() {
  if (!syncEnabled) return false;
  try {
    const { data, error } = await supabase.from('ghxst_store').select('id, value');
    if (error) { console.error('[GHXST Sync] pull failed', error.message); return false; }
    if (!data?.length) return false;
    data.forEach(row => {
      const key = row.id.split('::')[1];
      if (key) localStorage.setItem(key, JSON.stringify(row.value));
    });
    console.log('[GHXST Sync] restored', data.length, 'records from cloud');
    return true;
  } catch (err) { console.error('[GHXST Sync] pull error', err); return false; }
}

// One-time seed: push everything currently in this browser up to the cloud.
// Returns the number of records pushed. Safe to run more than once (upsert).
export async function pushAllLocal() {
  if (!syncEnabled) return 0;
  const rows = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith('ghxst_') || k === 'ghxst_user_id') continue;
    let value; try { value = JSON.parse(localStorage.getItem(k)); } catch { value = localStorage.getItem(k); }
    rows.push({ id: USER_ID + '::' + k, user_id: USER_ID, value, updated_at: new Date().toISOString() });
  }
  if (!rows.length) return 0;
  const { error } = await supabase.from('ghxst_store').upsert(rows);
  if (error) { console.error('[GHXST Sync] seed failed', error.message); return 0; }
  console.log('[GHXST Sync] seeded', rows.length, 'records to cloud');
  return rows.length;
}
